import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Box, useTheme } from '@mui/material';

/**
 * Reusable loading spinner component with customizable size and color
 * Using Material UI's CircularProgress
 * 
 * @param {Object} props
 * @param {string} props.size - Size of spinner ('sm', 'md', 'lg', 'xl')
 * @param {string} props.color - Color of spinner (Material UI color)
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element}
 */
const LoadingSpinner = ({ size = 'md', color = 'primary', className = '' }) => {
  // Size mapping to pixel values
  const sizeMap = {
    sm: 24,
    md: 40,
    lg: 56,
    xl: 72
  };
  
  const sizeValue = sizeMap[size] || sizeMap.md;
  const theme = useTheme();
  
  // Only use color prop if theme is fully loaded, otherwise use undefined
  // This prevents the "Cannot read properties of undefined (reading 'main')" error
  const safeColorProp = theme && theme.palette && theme.palette[color] ? color : undefined;
  
  return (
    <Box 
      className={className} 
      role="status" 
      aria-label="Loading" 
      display="flex" 
      justifyContent="center" 
      alignItems="center"
    >
      <CircularProgress 
        size={sizeValue}
        color={safeColorProp}
        thickness={4}
        sx={{
          color: !safeColorProp ? '#0d294e' : undefined // Fallback color if theme not available
        }}
      />
      <span className="sr-only">Loading...</span>
    </Box>
  );
};

LoadingSpinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  color: PropTypes.oneOf([
    'primary', 
    'secondary', 
    'error', 
    'info', 
    'success', 
    'warning',
    'inherit'
  ]),
  className: PropTypes.string
};

export default LoadingSpinner;
